// Settings popover behind the gear in the top controls.
//
// Replaced the reload button (2026-08-23). Reload restarted the whole
// AR session — a dev-era escape hatch sitting one icon away from Close,
// meaning nothing to a shopper and looking destructive next to it.
//
// Today it holds one choice: how you adjust a placed wheel. Touch is
// the default; Arrows brings back the joystick pad for discrete nudges.

import { Check, Hand, Move } from 'lucide-react'
import type { WheelAdjustMode } from '@/models/wheel-adjust-mode'

interface SettingsPopoverProps {
  mode: WheelAdjustMode
  onSelect: (mode: WheelAdjustMode) => void
  onDismiss: () => void
}

const OPTIONS: ReadonlyArray<{
  mode: WheelAdjustMode
  label: string
  Icon: typeof Hand
}> = [
  { mode: 'touch', label: 'Touch Adjust Wheel', Icon: Hand },
  { mode: 'arrows', label: 'Arrow Adjust Wheel', Icon: Move },
]

export function SettingsPopover({ mode, onSelect, onDismiss }: SettingsPopoverProps) {
  return (
    <>
      {/* Full-screen scrim: a tap anywhere else closes the popover
          without changing the setting, and stops that tap reaching the
          AR canvas underneath and placing a wheel. */}
      <div
        data-testid="settings-scrim"
        onClick={onDismiss}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'transparent',
          pointerEvents: 'auto',
          zIndex: 1200,
        }}
      />
      <div
        data-testid="settings-popover"
        role="menu"
        style={{
          position: 'fixed',
          top: 96,
          left: 12,
          minWidth: 210,
          background: 'rgba(0, 0, 0, 0.9)',
          borderRadius: 12,
          padding: 6,
          boxShadow: '0 4px 16px rgba(0,0,0,0.45)',
          pointerEvents: 'auto',
          zIndex: 1201,
        }}
      >
        {OPTIONS.map(({ mode: optionMode, label, Icon }) => {
          const active = optionMode === mode
          return (
            <button
              key={optionMode}
              type="button"
              role="menuitemradio"
              aria-checked={active}
              data-testid={`settings-option-${optionMode}`}
              onClick={() => {
                onSelect(optionMode)
                // Close on pick — a second tap to dismiss is a step
                // nobody wants after already choosing.
                onDismiss()
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                width: '100%',
                padding: '10px 10px',
                background: 'transparent',
                border: 'none',
                borderRadius: 8,
                color: 'white',
                fontSize: 14,
                textAlign: 'left',
                cursor: 'pointer',
              }}
            >
              <Icon size={16} strokeWidth={2} />
              <span style={{ flex: 1 }}>{label}</span>
              {/* Reserve the checkmark's width on every row so the
                  labels do not shift when the choice moves. */}
              <span
                style={{
                  width: 16,
                  display: 'inline-flex',
                  justifyContent: 'center',
                }}
              >
                {active ? <Check size={16} strokeWidth={2.5} /> : null}
              </span>
            </button>
          )
        })}
      </div>
    </>
  )
}
